/**
 * `<BaseEditPanel>` — the shared host-agnostic **GM mid-fight base-edit panel**, the webapp surface
 * for `edit_combatant_base`: pick a weapon and equip it onto the **live combatant base**
 * (combat-only by default — the source mech is untouched, and that divergence is the feature). The
 * **"also persist to base mech" checkbox** is the cross-domain write-back: when ticked, equipping
 * ALSO issues a roster `equip_weapon` against the chosen source mech so a future spawn carries it.
 *
 * It owns only its open/draft state; the combatant, `actorId`, the two dispatches, the compendium,
 * and the roster pilots come in as props (the editor-kit discipline), and it builds its commands
 * through the pure {@link buildBaseEditCommands}/{@link writeBackTargets}/{@link weaponOptions}
 * model. Collapsed by default; mirrors the reducer's rules as affordances and surfaces an
 * `{ ok:false }` from either dispatch inline (the host's `onError`).
 */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { tipAttrs } from "../tooltip";
import { useMemo, useState } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { Combatant, PilotData, RosterCommand } from "@lancer-vtt/domain";
import type { Result } from "@lancer-vtt/shared";
import { buildBaseEditCommands, weaponOptions, writeBackTargets } from "./baseEdit";
import type { EncounterDispatch } from "./StatusEditor";

/** The host's roster dispatch — applies a roster command and reports the reducer's accept/reject. */
export type RosterDispatch = (command: RosterCommand) => Result<void>;

export function BaseEditPanel({
  combatant: c,
  actorId,
  dispatch,
  rosterDispatch,
  pilots,
  compendium,
  onError,
}: {
  combatant: Combatant;
  actorId: string | null;
  dispatch: EncounterDispatch;
  rosterDispatch: RosterDispatch;
  pilots: PilotData[];
  compendium: Compendium | null;
  onError: (msg: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [weaponId, setWeaponId] = useState("");
  const [persist, setPersist] = useState(false);
  // The write-back target (only used when persisting): a pilot/mech the GM names.
  const [targetMechKey, setTargetMechKey] = useState("");

  const weapons = useMemo(() => (compendium ? weaponOptions(compendium) : []), [compendium]);
  const mechTargets = useMemo(() => writeBackTargets(pilots), [pilots]);

  const equip = () => {
    if (!actorId || !compendium || !weaponId) return;
    const target = persist ? mechTargets.find((t) => t.key === targetMechKey) : undefined;
    if (persist && !target) {
      onError("persist-to-base: pick a source mech with a free weapon slot");
      return;
    }
    const built = buildBaseEditCommands({ actorId, combatant: c, compendium, weaponId, writeBack: target });
    if (!built.ok) {
      onError(built.error);
      return;
    }
    // The encounter half (always): edit the live combatant base.
    const res = dispatch(built.value.edit);
    if (!res.ok) {
      onError(res.error);
      return;
    }
    // The roster half (only when persist is ticked): write the weapon back to the source mech.
    if (built.value.equip) {
      const rr = rosterDispatch(built.value.equip);
      if (!rr.ok) {
        onError(`persist-to-base: ${rr.error}`);
        return;
      }
    }
    onError(null);
    setWeaponId("");
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        disabled={!actorId}
        className="cw-baseedit-toggle"
        title="GM: equip a weapon onto this combatant mid-fight"
      >
        Edit base (mid-fight)…
      </button>
    );
  }

  const weaponCount = (c.base.weapons ?? []).length;
  return (
    <div className="cw-panel is-base">
      <div className="cw-armed-head">
        <span className="cw-armed-title is-muted">
          Edit base · {weaponCount} weapon{weaponCount === 1 ? "" : "s"}
        </span>
        <button onClick={() => setOpen(false)} className="cw-x" aria-label="close edit base" {...tipAttrs({ label: "Close base edit" })}>
          <Icon icon={faXmark} />
        </button>
      </div>
      <div className="cw-armed-row">
        <select
          value={weaponId}
          onChange={(e) => setWeaponId(e.target.value)}
          className="cw-field is-w14"
        >
          <option value="">pick a weapon…</option>
          {weapons.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        <button
          onClick={equip}
          disabled={!actorId || !weaponId || (persist && !targetMechKey)}
          className="cw-cta"
        >
          Equip {persist ? "+ persist" : "(combat-only)"}
        </button>
      </div>
      <label className="cw-baseedit-check">
        <input type="checkbox" checked={persist} onChange={(e) => setPersist(e.target.checked)} />
        also persist to base mech (writes the source mech too)
      </label>
      {persist && (
        <select
          value={targetMechKey}
          onChange={(e) => setTargetMechKey(e.target.value)}
          className="cw-field is-w16"
        >
          <option value="">pick the source mech…</option>
          {mechTargets.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
      )}
      <p className="cw-baseedit-note">
        Combat-only by default — the source mech is unchanged (the divergence is intended). Tick the box to
        write the weapon back to a roster mech too.
      </p>
    </div>
  );
}
